import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import ModalPortal from "../common/ModalPortal";

/**
 * Procedures column cell for the Hospitalisation history table.
 *
 * Rules:
 *   • Cell shows at most {MAX_VISIBLE} procedures; each item is 2-line
 *     clamped with a "…" tail via `.hs3-proc-list li`.
 *   • When total > MAX_VISIBLE, a "+ N more" pill sits under the list.
 *     Click opens the full-list dialog.
 *   • When total ≤ MAX_VISIBLE but at least one visible item is
 *     truncated (2-line clamp active), a plain "more" pill appears
 *     instead. Same dialog target.
 *   • No button when everything fits without clipping — the cell is
 *     already showing the full picture.
 *
 * The old CSS hover popover (`.hs3-proc-pop`) is retired in favour of
 * the click-driven dialog: hover-reveal fails on touch, and the CPT
 * chip only reads at dialog size anyway.
 */
const MAX_VISIBLE = 3;

const ProceduresCell = ({ procedures }) => {
  const list = procedures || [];
  const visible = list.slice(0, MAX_VISIBLE);
  const overflow = Math.max(0, list.length - MAX_VISIBLE);

  const itemRefs = useRef([]);
  const [hasTruncation, setHasTruncation] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Measure each visible <li> after paint to decide if the 2-line clamp
  // is currently biting. `scrollHeight - clientHeight > 1` — a 1px slack
  // avoids subpixel-rounding false positives on some browsers.
  useLayoutEffect(() => {
    const truncated = itemRefs.current
      .slice(0, visible.length)
      .some((el) => el && el.scrollHeight - el.clientHeight > 1);
    setHasTruncation(truncated);
    // Depending on `visible.length` alone would miss the case where the
    // same list resized without the count changing (row width change).
    // Re-measure whenever the array identity flips too.
  }, [visible.length, procedures]);

  if (list.length === 0) return <span>—</span>;

  const showOverflowPill = overflow > 0;
  const showMorePill = !showOverflowPill && hasTruncation;

  return (
    <>
      <ul className="hs3-proc-list">
        {visible.map((p, i) => (
          <li
            key={p.code || p.name || i}
            ref={(el) => { itemRefs.current[i] = el; }}
          >
            {p.name}
          </li>
        ))}
      </ul>
      {showOverflowPill && (
        <button
          type="button"
          className="hs3-proc-more"
          onClick={() => setDialogOpen(true)}
        >
          + {overflow} more
        </button>
      )}
      {showMorePill && (
        <button
          type="button"
          className="hs3-proc-more"
          onClick={() => setDialogOpen(true)}
        >
          more
        </button>
      )}
      {dialogOpen && (
        <ProceduresDialog
          procedures={list}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
};

ProceduresCell.propTypes = {
  /** Ordered list of procedures for this row. Each entry: `{ code?, name }`. */
  procedures: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    })
  ),
};

// ─── Dialog ────────────────────────────────────────────────────────────
// Bullet list of every procedure with the CPT code pill trailing each
// item that carries a code. Header title + X close. Backdrop click and
// Escape both dismiss. Same shell shape as the CC / Dx review dialogs so
// the section reads as a sibling of the rest of the fleet.
const ProceduresDialog = ({ procedures, onClose }) => {
  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <ModalPortal>
      <div
        className="cc-modal-backdrop delc-backdrop"
        onMouseDown={onClose}
      >
        <div
          className="hs3-proc-dialog"
          onMouseDown={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="hs3-proc-dialog-title"
        >
          <header className="hs3-proc-dialog-head">
            <h3 className="hs3-proc-dialog-title" id="hs3-proc-dialog-title">
              Procedures during hospitalisation
            </h3>
            <button
              type="button"
              className="hs3-proc-dialog-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </header>
          <ul className="hs3-proc-dialog-list">
            {procedures.map((p, i) => (
              <li key={p.code || p.name || i}>
                <span className="hs3-proc-dialog-name">{p.name}</span>
                {p.code ? (
                  <span className="hs3-proc-dialog-code">CPT {p.code}</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ModalPortal>
  );
};

ProceduresDialog.propTypes = {
  procedures: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProceduresCell;
